import type { Fragment, Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { Decoration, type EditorView } from '@atlaskit/editor-prosemirror/view';

import type { ReadingAidsPopupPropsInState } from '../../../types';
import { KEYWORD_DECORATION_CLASS, KEYWORD_STYLER_CLASS } from '../../../utils/const';
import { getSelectionRect } from '../../../utils/selection';
import {
	closeReadingAidsPopup,
	openReadingAidsPopup,
	resetCurrentKeywordId,
	setCurrentKeywordId,
} from '../commands';
import { getPluginState } from '../plugin-factory';
import type { AIDefinitionsPluginState, KeywordMatch, TextGrouping } from '../types';

import {
	createKeywordStyler,
	resetKeywordStylerActive,
	setKeywordStylerActive,
} from './keyword-styler';

export function isKeywordNode(node: EventTarget | null): node is HTMLSpanElement {
	return Boolean(
		node && node instanceof HTMLSpanElement && node.classList.contains(KEYWORD_DECORATION_CLASS),
	);
}

function handleCloseReadingAidsPopup({ editorView }: { editorView: EditorView }) {
	resetKeywordStylerActive();
	resetCurrentKeywordId()(editorView.state, editorView.dispatch);
	closeReadingAidsPopup()(editorView.state, editorView.dispatch);
}

// props here is not a complete list, other common props are passed when rendering ReadingAidsPopup
// props are aligned with confluence/next/packages/contextual-reading-aids/src/ReadingAidsAcronymsHighlighter.tsx
// - some of which are from confluence/next/packages/contextual-reading-aids/src/RendererTextHighlighter.tsx
// props for AI define button are from confluence/next/packages/highlight-actions/src/HighlightActions.tsx
function getReadingAidsPopupPropsForKeyword({
	keywordStart,
	keywordEnd,
	editorView,
	aiDefinitionsPluginState,
}: {
	keywordStart: HTMLElement;
	keywordEnd: HTMLElement;
	editorView: EditorView;
	aiDefinitionsPluginState: AIDefinitionsPluginState;
}): ReadingAidsPopupPropsInState {
	const range = document.createRange();
	range.setStartBefore(keywordStart);
	range.setEndAfter(keywordEnd);

	const selectionRect = getSelectionRect(range);

	const selectedContext = keywordStart.parentElement;
	const additionalContext = selectedContext
		? selectedContext.textContent || selectedContext.innerText
		: '';

	return {
		selectedText: range.toString(),
		additionalContext,
		selectionRange: range,
		selectionRect,
		onResult: () => {},
		onClose: () => {
			handleCloseReadingAidsPopup({ editorView });
		},
		isAutohighlighted: true,
		sessionId: aiDefinitionsPluginState.sessionId,
	};
}

/**
 * when click keyword, show ReadingAidsPopup
 * - only show ReadingAidsPopup if there's no ReadingAidsPopup open
 * - currently, when there is a ReadingAidsPopup open, the renderer won't open another one
 * - if we want to close the current one and open a new one instead in the future, we can refine the check
 */
export const onClickKeywordDecoration = ({
	event,
	editorView,
}: {
	event: MouseEvent;
	editorView: EditorView;
}): boolean => {
	const aiDefinitionsPluginState = getPluginState(editorView.state);
	const { target } = event;

	if (
		isKeywordNode(target) &&
		target.dataset.id &&
		!aiDefinitionsPluginState.isReadingAidsPopupOpen
	) {
		const id = target.dataset.id;
		// there might be multiple consecutive keyword inline decorations if there are other decorations on the keyword text
		const keywordDecorationArr = document.querySelectorAll(`.${KEYWORD_DECORATION_CLASS}-${id}`);
		const keywordStart = keywordDecorationArr[0];
		const keywordEnd = keywordDecorationArr[keywordDecorationArr.length - 1];
		if (isKeywordNode(keywordStart) && isKeywordNode(keywordEnd)) {
			const readingAidsPopupProps = getReadingAidsPopupPropsForKeyword({
				keywordStart,
				keywordEnd,
				editorView,
				aiDefinitionsPluginState,
			});

			setKeywordStylerActive(id);
			setCurrentKeywordId({ keywordId: id })(editorView.state, editorView.dispatch);
			openReadingAidsPopup({ readingAidsPopupProps })(editorView.state, editorView.dispatch);

			event.preventDefault();
			event.stopPropagation();
			return true;
		}
	}

	// all other cases, if there is a ReadingAidsPopup open, close it
	if (aiDefinitionsPluginState.isReadingAidsPopupOpen) {
		handleCloseReadingAidsPopup({ editorView });
	}

	return false;
};

// the confluence example is setup to match I&<>"'`.-D as a keyword, visit the example to see the generated ID
function encodeID(s: string) {
	if (s === '') {
		return '_';
	}

	return s.replace(/[^a-zA-Z0-9-]/g, function (match) {
		return '_' + match[0].charCodeAt(0).toString(16);
	});
}

/**
 * If other decorations are applied on part of the keyword text.
 * - like comments / syntax and grammar checks / proactive AI checks / other unknown decorations
 * For example, API is matched by a keyword decoration, and a comment exists on the middle `P`.
 * Then there will be three consecutive span `<span>A</span>`, `<span>P</span>, `<span>I</span>`.
 * Which means there will be three consecutive keyword inline decorations.
 *
 * If we apply the linear-gradient bottom border on the inline decoration spans,
 * there will be three consecutive linear-gradient bottom-border like this
 * https://files.slack.com/files-pri/TE8HJA7RS-F07UKRQ7JCT/image.png
 *
 * So we have to use a separate widget decoration to render the linear-gradient bottom border
 */
export const createKeywordDecorations = ({ keywordMatch }: { keywordMatch: KeywordMatch }) => {
	const { from, to, text } = keywordMatch;
	const id = `${from}-${to}-${encodeID(text)}`;
	return [
		Decoration.inline(
			from,
			to,
			{
				class: `${KEYWORD_DECORATION_CLASS} ${KEYWORD_DECORATION_CLASS}-${id}`,
				'data-id': id,
			},
			{ key: `${KEYWORD_DECORATION_CLASS}-${id}` },
		),
		Decoration.widget(
			to,
			() => {
				return createKeywordStyler({ id });
			},
			{
				key: `${KEYWORD_STYLER_CLASS}-${id}`,
				destroy: () => {},
				stopEvent: () => true,
				ignoreSelection: true,
			},
		),
	];
};

/**
 * Check if the node is conflict with keyword decoration
 * - text node with annotation or link mark
 */
function isConflictWithKeywordDecoration(node: PMNode): boolean {
	return (
		node.isText &&
		node.marks.some((mark) => {
			return mark.type.name === 'annotation' || mark.type.name === 'link';
		})
	);
}

// code from findMatches in editor-plugin-find-replace/src/utils/index.ts
export function findKeywordMatches({
	content,
	regex,
	contentIndex = 0,
}: {
	content: PMNode | Fragment;
	regex: RegExp;
	contentIndex?: number;
}): KeywordMatch[] {
	// each keyword is matched only once
	const set = new Set<string>();
	const matches: KeywordMatch[] = [];
	let textGrouping: TextGrouping | null = null;

	const collectMatch = (textGrouping: TextGrouping | null) => {
		if (!textGrouping) {
			return;
		}
		let { text, pos: relativePos } = textGrouping;
		const pos = contentIndex + relativePos;

		const regexMatches = text.matchAll(regex);
		for (const regexMatch of regexMatches) {
			const text = regexMatch[0];
			if (!set.has(text)) {
				set.add(text);
				matches.push({
					from: pos + regexMatch.index,
					to: pos + regexMatch.index + text.length,
					text,
				});
			}
		}
	};

	content.descendants((node, pos) => {
		if (node.isText && node.text) {
			let text = node.text;

			if (isConflictWithKeywordDecoration(node)) {
				text = ' '.repeat(text.length);
			}

			if (textGrouping === null) {
				textGrouping = { pos, text };
			} else {
				textGrouping.text += text;
			}
		} else {
			if (textGrouping) {
				collectMatch(textGrouping);
			}
			textGrouping = null;
		}
	});
	// if there's a dangling text grouping and no non-text node to trigger collectMatch, manually collectMatch
	if (textGrouping) {
		collectMatch(textGrouping);
	}

	return matches;
}
